import { useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage, apiFieldErrors } from "../services/api";
import {
  deleteProductImage,
  reorderProductImages,
  setPrimaryImage,
  uploadProductImages,
} from "../services/productService";

/**
 * A product's gallery. Every action hits the API immediately and returns the
 * refreshed list, so the order and the primary flag always match the server.
 * Reordering is by drag-and-drop, with arrow buttons as the keyboard path.
 */
export default function ProductImageManager({ productId, images, onChange }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dragId, setDragId] = useState(null);

  // Every action shares the same guard, spinner and error handling.
  const run = async (action, fallbackKey) => {
    setError("");
    setBusy(true);
    try {
      onChange(await action());
    } catch (err) {
      const first = Object.values(apiFieldErrors(err)).flat()[0];
      setError(first || apiErrorMessage(err, t(fallbackKey)));
    } finally {
      setBusy(false);
    }
  };

  const upload = (e) => {
    const files = Array.from(e.target.files);
    e.target.value = "";
    if (files.length) run(() => uploadProductImages(productId, files), "images.uploadFailed");
  };

  const move = (id, direction) => {
    const index = images.findIndex((image) => image.id === id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= images.length) return;

    const ids = images.map((image) => image.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    run(() => reorderProductImages(productId, ids), "images.reorderFailed");
  };

  const drop = (targetId) => {
    if (!dragId || dragId === targetId) return;

    const ids = images.map((image) => image.id).filter((id) => id !== dragId);
    ids.splice(ids.indexOf(targetId), 0, dragId);
    setDragId(null);
    run(() => reorderProductImages(productId, ids), "images.reorderFailed");
  };

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <label className="field-label mb-0" htmlFor="product-images">
          {t("images.title")}
          <span className="ms-2 rounded-full bg-raised px-2 py-1 text-xs text-ink-2">
            {images.length}
          </span>
        </label>
        <span className="text-xs text-ink-2">{t("images.hint")}</span>
      </div>

      <input
        id="product-images"
        type="file"
        multiple
        disabled={busy}
        accept=".jpg,.jpeg,.png,.webp"
        className="field-input"
        onChange={upload}
      />

      {error && (
        <div className="mt-3 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error}
        </div>
      )}

      {images.length === 0 ? (
        <p className="mt-3 text-sm text-ink-2">{t("images.empty")}</p>
      ) : (
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {images.map((image, index) => (
            <div
              key={image.id}
              draggable={!busy}
              onDragStart={() => setDragId(image.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => drop(image.id)}
              className={`group relative overflow-hidden rounded-lg border ${
                image.is_primary ? "border-accent ring-1 ring-accent-line" : "border-line"
              } ${dragId === image.id ? "opacity-50" : ""}`}
            >
              <img src={image.url} alt="" className="h-28 w-full object-cover" />

              {image.is_primary && (
                <span className="absolute start-1.5 top-1.5 rounded-full bg-accent px-2 py-1 text-caption font-semibold text-accent-on">
                  {t("images.primary")}
                </span>
              )}

              <div className="flex items-center justify-between gap-1 px-2 py-1">
                <div className="flex gap-1">
                  <button
                    type="button"
                    disabled={busy || index === 0}
                    onClick={() => move(image.id, -1)}
                    aria-label={t("images.moveEarlier")}
                    className="rounded px-2 text-xs text-ink-2 hover:bg-raised disabled:text-ink-2 disabled:hover:bg-transparent"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    disabled={busy || index === images.length - 1}
                    onClick={() => move(image.id, 1)}
                    aria-label={t("images.moveLater")}
                    className="rounded px-2 text-xs text-ink-2 hover:bg-raised disabled:text-ink-2 disabled:hover:bg-transparent"
                  >
                    ›
                  </button>
                </div>

                <div className="flex gap-1">
                  {!image.is_primary && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        run(() => setPrimaryImage(productId, image.id), "images.primaryFailed")
                      }
                      className="text-caption font-medium text-accent-text hover:underline disabled:text-ink-2 disabled:hover:bg-transparent"
                    >
                      {t("images.makePrimary")}
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      run(() => deleteProductImage(productId, image.id), "images.deleteFailed")
                    }
                    className="text-caption font-medium text-danger-text hover:underline disabled:text-ink-2 disabled:hover:bg-transparent"
                  >
                    {t("common.delete")}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
